import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import { avatarTone, initialsOf } from '../../../common/libs/initials';
import { IconChevron, IconEdit, IconUsers } from '../../../common/icons';
import DeactivateButton from '../../../common/components/deactivate/deactivate-button';
import { teamKeys } from '../queries/keys';
import { teamPaths } from '../queries/paths';
import { TEAMS_PAGE_SIZE, useTeamMembers, useTeamsList } from '../contexts/teams';
import SearchBox from '../../../common/components/search-box/search-box';

import '../styles/overview.css';

// Cards, like clients: what matters about a team is each one on its own — its
// name and what it is for — and not comparing a column of them.
//
// The people count comes with the list since tier 12 (`members_count`). WHO
// those people are now opens in place: it is the question that follows the
// count every single time, and sending somebody to another screen for it meant
// losing the search and the filter on the way.

const STATUS_LABEL = {
    2: 'Activo',
    1: 'Inactivo',
    0: 'Eliminado',
};

const DATE_FORMAT = new Intl.DateTimeFormat('es', {
    day: 'numeric', month: 'short', year: 'numeric',
});

// Both spellings are read because the published contract documents no response
// shape: Sequelize rows answer createdAt, hand-built packages answer created_at.
function formatDate(team) {
    const raw = team.createdAt ?? team.created_at;
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : DATE_FORMAT.format(date);
}

// EL FILTRO CORRE ACÁ, y no por gusto: `GET /admin/teams` acepta `limit`,
// `offset` y `q`, y nada más — no hay parámetro de estado. So it acts on the
// page that was loaded, and the screen says so instead of letting a filtered
// count read as a total.
const STATUS_FILTERS = [
    { value: '',  label: 'Todos' },
    { value: '2', label: 'Activos' },
    { value: '1', label: 'Inactivos' },
];

function TeamCard({ team }) {
    const [open, setOpen] = useState(false);
    const since = formatDate(team);
    const memberCount = team.members_count;
    const panelId = `team-${team.id}-members`;

    // Only asked for once the card is open, and cached under the same key the
    // team's own screen uses — so opening it there afterwards costs nothing.
    const { data, isLoading, error } = useTeamMembers(open ? team.id : null);
    const members = data?.members ?? [];

    return (
        <article className={`team-card ${open ? 'team-card--open' : ''}`}>
            <header className="team-card__head">
                <button
                    type="button"
                    className="team-card__toggle"
                    onClick={() => setOpen(value => !value)}
                    aria-expanded={open}
                    aria-controls={panelId}
                    aria-label={`${open ? 'Ocultar' : 'Ver'} los miembros de ${team.name}`}
                >
                    <span className={`team-card__chevron ${open ? 'team-card__chevron--open' : ''}`}>
                        <IconChevron />
                    </span>
                </button>

                <span
                    className={`avatar team-card__avatar avatar--tone-${avatarTone(team.name)}`}
                    aria-hidden="true"
                >
                    {initialsOf(team.name)}
                </span>

                <span className="team-card__identity">
                    <span className="team-card__name">{team.name}</span>
                    <span className="team-card__people">
                        <IconUsers />
                        {memberCount == null
                            ? '—'
                            : `${memberCount} ${memberCount === 1 ? 'persona' : 'personas'}`}
                    </span>
                </span>

                <span className="row-actions">
                    <Gate domain="teams:write">
                        <Link
                            to={`/admin/teams/${team.id}`}
                            className="team-card__edit"
                            aria-label={`Editar ${team.name}`}
                            title="Editar"
                        >
                            <IconEdit />
                        </Link>
                    </Gate>
                    <Gate domain="teams:admin">
                        <DeactivateButton
                            name={team.name}
                            noun="equipo"
                            path={teamPaths.detail(team.id)}
                            invalidate={[teamKeys.all]}
                        />
                    </Gate>
                </span>
            </header>

            {team.description && (
                <div className="team-card__body">
                    <p className="team-card__description">{team.description}</p>
                </div>
            )}

            <footer className="team-card__foot">
                <span className="team-card__fact">
                    <span className="team-card__block-label">Estado</span>
                    <span className={`team-card__status team-card__status--${team.status}`}>
                        {STATUS_LABEL[team.status] ?? 'Desconocido'}
                    </span>
                </span>

                <span className="team-card__fact">
                    <span className="team-card__block-label">Desde</span>
                    <span className="team-card__value">{since || '—'}</span>
                </span>
            </footer>

            {open && (
                <div className="team-card__members" id={panelId}>
                    {isLoading && <p className="team-card__muted">Buscando...</p>}

                    {error && (
                        <p className="team-card__muted">
                            {error.message || 'No se pudieron leer los miembros'}
                        </p>
                    )}

                    {!isLoading && !error && members.length === 0 && (
                        <p className="team-card__muted">Este equipo todavía no tiene a nadie.</p>
                    )}

                    {members.length > 0 && (
                        <ul className="team-card__member-list">
                            {members.map(member => (
                                <li key={member.id} className="team-card__member">
                                    <span
                                        className={`avatar team-card__member-avatar avatar--tone-${avatarTone(member.user?.name || member.user?.email)}`}
                                        aria-hidden="true"
                                    >
                                        {initialsOf(member.user?.name || member.user?.email)}
                                    </span>
                                    <span className="team-card__member-name">
                                        {member.user?.name ?? `Usuario #${member.user_id}`}
                                    </span>
                                    {/* Only `lead` grants anything — the other
                                        five describe what somebody does and are
                                        read by nothing that decides a
                                        permission. Marked so the list does not
                                        read as six equivalent labels. */}
                                    <span className={`team-card__member-role ${member.team_role === 'lead' ? 'team-card__member-role--lead' : ''}`}>
                                        {member.team_role}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* Said because the count above and this list could look
                        like they disagree, and they do not: both show only the
                        active members. Whoever was removed keeps their row with
                        status 1 and appears in neither. */}
                    <p className="team-card__muted">
                        Solo los miembros activos. Quien fue quitado conserva su registro y no
                        aparece acá ni en la cuenta.
                    </p>
                </div>
            )}
        </article>
    );
}

export default function TeamsOverview() {
    const [params, setParams] = useSearchParams();

    const page = Math.max(Number(params.get('page')) || 1, 1);
    const q = params.get('q') || '';
    const status = params.get('status') || '';
    const offset = (page - 1) * TEAMS_PAGE_SIZE;

    const { data, isLoading, isFetching, error } = useTeamsList({
        limit: TEAMS_PAGE_SIZE,
        offset,
        q,
    });

    const loaded = useMemo(() => data?.items ?? [], [data]);
    const total = data?.pagination?.total ?? 0;

    const items = useMemo(
        () => (status ? loaded.filter(team => String(team.status) === status) : loaded),
        [loaded, status],
    );

    const lastPage = useMemo(
        () => Math.max(Math.ceil(total / TEAMS_PAGE_SIZE), 1),
        [total],
    );

    const setParam = (key, value) => {
        const target = new URLSearchParams(params);
        if (value) target.set(key, value);
        else target.delete(key);
        target.delete('page');
        setParams(target);
    };

    const goToPage = (next) => {
        const target = new URLSearchParams(params);
        if (next <= 1) target.delete('page');
        else target.set('page', String(next));
        setParams(target);
    };

    if (isLoading && !data) return <p className="teams__loading">Cargando equipos...</p>;

    if (error) {
        return (
            <p className="teams__error">
                {error.message || 'No se pudieron cargar los equipos'}
            </p>
        );
    }

    return (
        <section className="teams">
            <header className="teams__header">
                <div className="teams__heading">
                    <span className="teams__count">
                        {total} {total === 1 ? 'equipo' : 'equipos'}
                    </span>
                    <h2 className="teams__title">Equipos</h2>
                </div>

                <Gate domain="teams:write">
                    <Link to="/admin/teams/new" className="teams__new">+ Nuevo equipo</Link>
                </Gate>
            </header>

            <SearchBox placeholder="Buscar por nombre" />

            <div className="teams__filters">
                <label className="teams__filter">
                    <span className="teams__filter-label">Estado</span>
                    <select
                        className="teams__filter-input"
                        value={status}
                        onChange={e => setParam('status', e.target.value)}
                    >
                        {STATUS_FILTERS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </label>
            </div>

            {status && total > loaded.length && (
                <p className="teams__scope-note">
                    El estado se aplica sobre <strong>esta página</strong> de {loaded.length}, no
                    sobre los {total} equipos: el listado del backend no acepta filtro de estado.
                </p>
            )}

            {!items.length && (
                <p className="teams__empty">
                    {status && loaded.length
                        ? 'Ningún equipo de esta página coincide con el filtro.'
                        : q
                            ? `Ningún equipo coincide con "${q}".`
                            : 'Todavía no hay equipos cargados.'}
                </p>
            )}

            <div className={`teams__grid ${isFetching ? 'teams__grid--refreshing' : ''}`}>
                {items.map(team => <TeamCard key={team.id} team={team} />)}
            </div>

            {total > TEAMS_PAGE_SIZE && (
                <nav className="teams__pager">
                    <button
                        type="button"
                        className="teams__pager-button"
                        onClick={() => goToPage(page - 1)}
                        disabled={page <= 1}
                    >
                        Anterior
                    </button>

                    <span className="teams__pager-position">
                        Página {page} de {lastPage}
                    </span>

                    <button
                        type="button"
                        className="teams__pager-button"
                        onClick={() => goToPage(page + 1)}
                        disabled={page >= lastPage}
                    >
                        Siguiente
                    </button>
                </nav>
            )}
        </section>
    );
}
